/**
 * Shared Auth Types and Utilities for Frontend Applications
 * All auth operations go through the backend API (no direct Supabase access)
 */

import { createApiClient, type ApiClient, type ApiResponse } from './api';

// ============================================
// Auth Types
// ============================================

export interface AuthUser {
  id: string;
  email: string;
  emailConfirmedAt?: string | null;
  pendingEmail?: string | null;
  role?: string;
  created_at?: string;
  updated_at?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
}

// Backend response structure for login
export interface LoginResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  sessionKey?: string | null;
}

// Backend response structure for register
export interface RegisterResponse {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  needsVerification: boolean;
  sessionKey?: string | null;
}

export interface OAuthProvider {
  provider: 'google' | 'github';
  redirectTo?: string;
  /** Allow registration via OAuth (default: false - login only). Set to true from signup page. */
  allowRegistration?: boolean;
}

// Response from OAuth tokens verification
export interface OAuthTokensResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  isNewUser: boolean;
  requiresLegalAcceptance: boolean;
  sessionKey?: string | null;
}

// Legal acceptance data
export interface LegalAcceptanceData {
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  confirmedAge?: boolean;
}

// Identity types for linked accounts
export interface LinkedIdentity {
  provider: string;
  email: string;
  createdAt: string;
  lastSignInAt?: string;
}

export interface IdentitiesResponse {
  identities: LinkedIdentity[];
  hasPassword: boolean;
  pendingEmail?: string | null;
  providers: string[];
}

export interface GoogleLinkResponse {
  url: string;
  provider: 'google';
  expectedEmail: string;
  message: string;
  linkAttemptId: string;
}

// ============================================
// Auth State Management
// ============================================

export interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  sessionKey: string | null;
  expiresAt: number | null;
  initialized: boolean;
  loading: boolean;
  error: string | null;
}

export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: 'plutos_access_token',
  REFRESH_TOKEN: 'plutos_refresh_token',
  SESSION_KEY: 'plutos_session_key',
  USER: 'plutos_user',
  EXPIRES_AT: 'plutos_expires_at',
} as const;

const ACCESS_TOKEN_REFRESH_SKEW_MS = 5 * 60 * 1000;
const ACCESS_TOKEN_REFRESH_POLL_MS = 60 * 1000;

// ============================================
// Auth Service Factory
// ============================================

export interface AuthServiceConfig {
  apiBaseUrl: string;
  onAuthChange?: (user: AuthUser | null) => void;
  storage?: Storage;
  useCookieRefreshTokens?: boolean;
  /** Extra headers on every auth API request (e.g. Android TWA client hints). */
  getClientHeaders?: () => Record<string, string>;
}

export interface AuthService {
  // State
  getState(): AuthState;
  getUser(): AuthUser | null;
  getAccessToken(): string | null;
  getSessionKey(): string | null;
  isAuthenticated(): boolean;
  isEmailVerified(): boolean;
  
  // Actions
  initialize(): Promise<AuthUser | null>;
  login(credentials: LoginCredentials): Promise<LoginResponse>;
  register(credentials: RegisterCredentials): Promise<RegisterResponse>;
  logout(): Promise<void>;
  refreshToken(): Promise<boolean>;
  
  // OAuth
  getOAuthUrl(provider: OAuthProvider): Promise<string>;
  handleOAuthCallback(params: URLSearchParams): Promise<LoginResponse>;
  /** Handle OAuth tokens from implicit flow (tokens in URL hash) */
  handleOAuthTokens(accessToken: string, refreshToken: string, allowRegistration?: boolean): Promise<OAuthTokensResponse>;
  /** Accept legal terms (required for new OAuth users) */
  acceptLegal(data: LegalAcceptanceData): Promise<void>;
  
  // Password
  resetPassword(email: string): Promise<void>;
  updatePassword(newPassword: string): Promise<void>;
  
  // Email
  resendVerification(): Promise<void>;
  requestEmailChange(newEmail: string): Promise<void>;
  
  // Identity linking
  getLinkedIdentities(): Promise<IdentitiesResponse>;
  linkGoogle(): Promise<GoogleLinkResponse>;
  /** @deprecated Supabase automatic linking is handled via redirect + identity refresh. */
  handleLinkCallback(code: string, provider?: string): Promise<void>;
  unlinkIdentity(provider: string): Promise<void>;
  setPassword(password: string): Promise<void>;
  hasPassword(): Promise<boolean>;
}

/**
 * Create an auth service instance
 */
export function createAuthService(config: AuthServiceConfig): AuthService {
  const {
    apiBaseUrl,
    onAuthChange,
    storage = localStorage,
    useCookieRefreshTokens = false,
    getClientHeaders,
  } = config;
  
  // Internal state
  let state: AuthState = {
    user: null,
    accessToken: null,
    refreshToken: null,
    sessionKey: null,
    expiresAt: null,
    initialized: false,
    loading: false,
    error: null,
  };
  
  // Token refresh interval + visibility listeners
  let refreshInterval: ReturnType<typeof setInterval> | null = null;
  let visibilityHandler: (() => void) | null = null;
  let focusHandler: (() => void) | null = null;
  let refreshInFlight: Promise<boolean> | null = null;
  // Bumped on login/logout so a slow initialize() cannot wipe a newer session.
  let authEpoch = 0;
  
  // API client - uses /api/v1 prefix
  const api: ApiClient = createApiClient({
    baseUrl: apiBaseUrl,
    getToken: () => state.accessToken,
    getSessionKey: () => state.sessionKey,
    getClientHeaders,
  });
  
  // Helper functions
  function updateState(updates: Partial<AuthState>) {
    state = { ...state, ...updates };
  }

  function readJwtExpiry(accessToken: string | null | undefined): number | null {
    if (!accessToken) return null;
    try {
      const payload = accessToken.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      const json = typeof atob === 'function'
        ? atob(padded)
        : typeof Buffer !== 'undefined'
          ? Buffer.from(padded, 'base64').toString('utf8')
          : null;
      if (!json) return null;
      const parsed = JSON.parse(json);
      return typeof parsed.exp === 'number' ? parsed.exp : null;
    } catch {
      return null;
    }
  }

  function resolveExpiresAt(accessToken: string | null | undefined, expiresAt?: number | null): number | null {
    if (typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt > 0) {
      return Math.floor(expiresAt);
    }
    return readJwtExpiry(accessToken);
  }
  
  function saveToStorage(
    user: AuthUser | null,
    accessToken: string | null,
    refreshToken: string | null,
    sessionKey: string | null,
    expiresAt?: number | null,
  ) {
    if (accessToken) {
      storage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    } else {
      storage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    }
    
    if (!useCookieRefreshTokens && refreshToken) {
      storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    } else {
      storage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    }

    if (sessionKey) {
      storage.setItem(AUTH_STORAGE_KEYS.SESSION_KEY, sessionKey);
    } else {
      storage.removeItem(AUTH_STORAGE_KEYS.SESSION_KEY);
    }
    
    if (user) {
      storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user));
    } else {
      storage.removeItem(AUTH_STORAGE_KEYS.USER);
    }

    const resolvedExpiry = resolveExpiresAt(accessToken, expiresAt ?? null);
    if (resolvedExpiry) {
      storage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, String(resolvedExpiry));
    } else {
      storage.removeItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
    }
  }
  
  function loadFromStorage(): {
    user: AuthUser | null;
    accessToken: string | null;
    refreshToken: string | null;
    sessionKey: string | null;
    expiresAt: number | null;
  } {
    const accessToken = storage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    const sessionKey = storage.getItem(AUTH_STORAGE_KEYS.SESSION_KEY);
    const userStr = storage.getItem(AUTH_STORAGE_KEYS.USER);
    const expiresAtRaw = storage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
    
    let user: AuthUser | null = null;
    if (userStr) {
      try {
        user = JSON.parse(userStr);
      } catch {
        // Invalid stored user
      }
    }

    const storedExpiry = expiresAtRaw ? Number(expiresAtRaw) : null;
    const expiresAt = resolveExpiresAt(
      accessToken,
      Number.isFinite(storedExpiry as number) ? storedExpiry : null,
    );
    
    return { user, accessToken, refreshToken, sessionKey, expiresAt };
  }

  function accessTokenNeedsRefresh(nowMs = Date.now()): boolean {
    if (!state.accessToken) return false;
    const expiresAt = state.expiresAt || resolveExpiresAt(state.accessToken);
    if (!expiresAt) {
      // Unknown expiry — refresh proactively when cookie mode is on.
      return useCookieRefreshTokens;
    }
    return expiresAt * 1000 <= nowMs + ACCESS_TOKEN_REFRESH_SKEW_MS;
  }

  async function refreshIfNeeded(reason: string): Promise<boolean> {
    if (!state.accessToken && !useCookieRefreshTokens && !state.refreshToken) {
      return false;
    }
    if (!accessTokenNeedsRefresh()) {
      return true;
    }
    console.log(`[Auth] Refreshing token (${reason})...`);
    return service.refreshToken();
  }
  
  function startTokenRefresh() {
    stopTokenRefresh();

    refreshInterval = setInterval(() => {
      refreshIfNeeded('interval').catch((err) => {
        console.warn('[Auth] Auto token refresh failed:', err);
      });
    }, ACCESS_TOKEN_REFRESH_POLL_MS);

    if (typeof document !== 'undefined') {
      visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          refreshIfNeeded('visibility').catch((err) => {
            console.warn('[Auth] Visibility refresh failed:', err);
          });
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }

    if (typeof window !== 'undefined') {
      focusHandler = () => {
        refreshIfNeeded('focus').catch((err) => {
          console.warn('[Auth] Focus refresh failed:', err);
        });
      };
      window.addEventListener('focus', focusHandler);
    }

    // Kick once immediately in case we restored a near-expired token.
    refreshIfNeeded('start').catch(() => undefined);
  }
  
  function stopTokenRefresh() {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
    if (focusHandler && typeof window !== 'undefined') {
      window.removeEventListener('focus', focusHandler);
      focusHandler = null;
    }
  }

  async function validateStoredSession(): Promise<boolean> {
    const response = await api.get<{ user: AuthUser }>('/api/v1/auth/me');

    if (!response.success || !response.data?.user) {
      return false;
    }

    const validatedUser = response.data.user;
    saveToStorage(validatedUser, state.accessToken, state.refreshToken, state.sessionKey, state.expiresAt);
    updateState({ user: validatedUser });
    onAuthChange?.(validatedUser);
    return true;
  }
  
  // Service implementation
  const service: AuthService = {
    getState() {
      return { ...state };
    },
    
    getUser() {
      return state.user;
    },
    
    getAccessToken() {
      return state.accessToken;
    },

    getSessionKey() {
      return state.sessionKey;
    },
    
    isAuthenticated() {
      return !!state.accessToken && !!state.user;
    },
    
    isEmailVerified() {
      return !!state.user?.emailConfirmedAt;
    },
    
    async initialize(): Promise<AuthUser | null> {
      if (state.initialized) {
        return state.user;
      }
      
      updateState({ loading: true });
      const epochAtStart = authEpoch;
      
      const { user, accessToken, refreshToken, sessionKey, expiresAt } = loadFromStorage();
      
      if (accessToken && user) {
        // We have stored credentials - restore state immediately
        updateState({
          user,
          accessToken,
          refreshToken,
          sessionKey,
          expiresAt,
          initialized: true,
          loading: false,
        });

        const sessionValid = await validateStoredSession();

        if (!sessionValid) {
          const refreshed = await service.refreshToken().catch(err => {
            console.warn('[Auth] Init refresh failed after session validation:', err);
            return false;
          });

          if (!refreshed) {
            // A login that completed while we were validating must win.
            if (epochAtStart !== authEpoch) {
              return state.user;
            }
            saveToStorage(null, null, null, null, null);
            updateState({
              user: null,
              accessToken: null,
              refreshToken: null,
              sessionKey: null,
              expiresAt: null,
              initialized: true,
              loading: false,
              error: null,
            });
            onAuthChange?.(null);
            return null;
          }
        }
        
        // Start token refresh timer (near-expiry + visibility/focus)
        if (state.refreshToken || useCookieRefreshTokens) {
          startTokenRefresh();
        }
        
        onAuthChange?.(state.user);
        return state.user;
      }
      
      updateState({ initialized: true, loading: false });
      return null;
    },
    
    async login(credentials: LoginCredentials): Promise<LoginResponse> {
      updateState({ loading: true, error: null });
      authEpoch += 1;
      
      const response = await api.post<LoginResponse>('/api/v1/auth/login', credentials, {
        includeAuth: false,
        includeSessionKey: false,
      });
      
      if (!response.success || !response.data) {
        updateState({ loading: false, error: response.error?.message || 'Login failed' });
        throw new Error(response.error?.message || 'Login failed');
      }
      
      const { user, accessToken, refreshToken, sessionKey, expiresAt } = response.data;
      const persistedRefreshToken = useCookieRefreshTokens ? null : refreshToken;
      const resolvedExpiresAt = resolveExpiresAt(accessToken, expiresAt);
      
      saveToStorage(user, accessToken, persistedRefreshToken, sessionKey || null, resolvedExpiresAt);
      updateState({
        user,
        accessToken,
        refreshToken: persistedRefreshToken,
        sessionKey: sessionKey || null,
        expiresAt: resolvedExpiresAt,
        loading: false,
        error: null,
        initialized: true,
      });
      
      startTokenRefresh();
      onAuthChange?.(user);
      
      return response.data;
    },
    
    async register(credentials: RegisterCredentials): Promise<RegisterResponse> {
      updateState({ loading: true, error: null });
      
      const response = await api.post<RegisterResponse>('/api/v1/auth/register', credentials, {
        includeAuth: false,
        includeSessionKey: false,
      });
      
      if (!response.success || !response.data) {
        updateState({ loading: false, error: response.error?.message || 'Registration failed' });
        throw new Error(response.error?.message || 'Registration failed');
      }
      
      // If email verification is not required, save session
      if (response.data.accessToken && response.data.refreshToken && response.data.user) {
        authEpoch += 1;
        const persistedRefreshToken = useCookieRefreshTokens ? null : response.data.refreshToken;
        const resolvedExpiresAt = resolveExpiresAt(response.data.accessToken, response.data.expiresAt);
        saveToStorage(
          response.data.user,
          response.data.accessToken,
          persistedRefreshToken,
          response.data.sessionKey || null,
          resolvedExpiresAt,
        );
        updateState({
          user: response.data.user,
          accessToken: response.data.accessToken,
          refreshToken: persistedRefreshToken,
          sessionKey: response.data.sessionKey || null,
          expiresAt: resolvedExpiresAt,
          loading: false,
          initialized: true,
        });
        startTokenRefresh();
        onAuthChange?.(response.data.user);
      } else {
        updateState({ loading: false });
      }
      
      return response.data;
    },
    
    async logout(): Promise<void> {
      stopTokenRefresh();
      authEpoch += 1;
      
      // Call backend logout endpoint
      await api.post('/api/v1/auth/logout').catch(() => {
        // Ignore errors, we're logging out anyway
      });
      
      saveToStorage(null, null, null, null, null);
      updateState({
        user: null,
        accessToken: null,
        refreshToken: null,
        sessionKey: null,
        expiresAt: null,
        error: null,
      });
      
      onAuthChange?.(null);
    },
    
    async refreshToken(): Promise<boolean> {
      if (!state.refreshToken && !useCookieRefreshTokens) {
        return false;
      }

      if (refreshInFlight) {
        return refreshInFlight;
      }

      refreshInFlight = (async () => {
        // Keep a stable logical session across refreshes, even if memory state was reset.
        const fallbackSessionKey = state.sessionKey || storage.getItem(AUTH_STORAGE_KEYS.SESSION_KEY) || null;
        if (!state.sessionKey && fallbackSessionKey) {
          updateState({ sessionKey: fallbackSessionKey });
        }
        
        const response = await api.post<LoginResponse>('/api/v1/auth/refresh', {
          ...(state.refreshToken ? { refreshToken: state.refreshToken } : {}),
        }, {
          includeAuth: false,
        });
        
        if (!response.success || !response.data) {
          console.warn('[Auth] Token refresh failed:', response.error?.message);
          return false;
        }
        
        const { user, accessToken, refreshToken, sessionKey, expiresAt } = response.data;
        const persistedRefreshToken = useCookieRefreshTokens ? null : refreshToken;
        const resolvedSessionKey = sessionKey || state.sessionKey || fallbackSessionKey || null;
        const resolvedExpiresAt = resolveExpiresAt(accessToken, expiresAt);
        
        saveToStorage(user, accessToken, persistedRefreshToken, resolvedSessionKey, resolvedExpiresAt);
        updateState({
          user,
          accessToken,
          refreshToken: persistedRefreshToken,
          sessionKey: resolvedSessionKey,
          expiresAt: resolvedExpiresAt,
        });
        
        return true;
      })().finally(() => {
        refreshInFlight = null;
      });

      return refreshInFlight;
    },
    
    async getOAuthUrl(provider: OAuthProvider): Promise<string> {
      // Backend exposes OAuth endpoints as GET /api/v1/auth/oauth/:provider
      // Pass allowRegistration query param to control whether new users can register
      const allowReg = provider.allowRegistration ? 'true' : 'false';
      const response = await api.get<{ url: string }>(`/api/v1/auth/oauth/${provider.provider}?allowRegistration=${allowReg}`, {
        includeAuth: false,
        includeSessionKey: false,
      });
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get OAuth URL');
      }
      
      return response.data.url;
    },
    
    async handleOAuthCallback(params: URLSearchParams): Promise<LoginResponse> {
      const code = params.get('code');
      if (!code) {
        throw new Error('No authorization code in callback');
      }
      
      // Check if registration was allowed (passed via redirect URL)
      const allowRegistration = params.get('allowRegistration') === 'true';
      
      const response = await api.post<LoginResponse>('/api/v1/auth/oauth/callback', {
        code,
        allowRegistration,
      }, {
        includeAuth: false,
        includeSessionKey: false,
      });
      
      if (!response.success || !response.data) {
        // Check for specific error codes and throw with code attached
        const errorCode = (response.error as any)?.code;
        const error = new Error(response.error?.message || 'OAuth callback failed');
        if (errorCode) {
          (error as any).code = errorCode;
        }
        throw error;
      }
      
      const { user, accessToken, refreshToken, sessionKey, expiresAt } = response.data;
      
      authEpoch += 1;
      const persistedRefreshToken = useCookieRefreshTokens ? null : refreshToken;
      const resolvedExpiresAt = resolveExpiresAt(accessToken, expiresAt);
      saveToStorage(user, accessToken, persistedRefreshToken, sessionKey || null, resolvedExpiresAt);
      updateState({
        user,
        accessToken,
        refreshToken: persistedRefreshToken,
        sessionKey: sessionKey || null,
        expiresAt: resolvedExpiresAt,
        initialized: true,
      });
      
      startTokenRefresh();
      onAuthChange?.(user);
      
      return response.data;
    },
    
    async handleOAuthTokens(accessTokenParam: string, refreshTokenParam: string, allowRegistration: boolean = false): Promise<OAuthTokensResponse> {
      // Verify tokens with backend - this checks if user is new and whether registration is allowed
      const response = await api.post<{ user: AuthUser; accessToken: string; refreshToken: string; isNewUser: boolean; requiresLegalAcceptance: boolean; sessionKey?: string | null }>('/api/v1/auth/oauth/verify-tokens', {
        accessToken: accessTokenParam,
        refreshToken: refreshTokenParam,
        allowRegistration,
      }, {
        includeAuth: false,
        includeSessionKey: false,
      });
      
      if (!response.success || !response.data) {
        // Check for specific error codes
        const errorCode = (response.error as any)?.code;
        
        // Clear any stored tokens on failure
        saveToStorage(null, null, null, null, null);
        updateState({
          user: null,
          accessToken: null,
          refreshToken: null,
          sessionKey: null,
          expiresAt: null,
        });
        
        const error = new Error(response.error?.message || 'Failed to verify OAuth tokens');
        if (errorCode) {
          (error as any).code = errorCode;
        }
        throw error;
      }
      
      const { user, isNewUser, requiresLegalAcceptance } = response.data;
      
      authEpoch += 1;
      const persistedRefreshToken = useCookieRefreshTokens ? null : refreshTokenParam;
      const resolvedExpiresAt = resolveExpiresAt(accessTokenParam, null);
      saveToStorage(user, accessTokenParam, persistedRefreshToken, response.data.sessionKey || null, resolvedExpiresAt);
      updateState({
        user,
        accessToken: accessTokenParam,
        refreshToken: persistedRefreshToken,
        sessionKey: response.data.sessionKey || null,
        expiresAt: resolvedExpiresAt,
        initialized: true,
      });
      
      startTokenRefresh();
      onAuthChange?.(user);
      
      return {
        user,
        accessToken: accessTokenParam,
        refreshToken: refreshTokenParam,
        isNewUser,
        requiresLegalAcceptance,
        sessionKey: response.data.sessionKey || null,
      };
    },
    
    async acceptLegal(data: LegalAcceptanceData): Promise<void> {
      const response = await api.post('/api/v1/auth/accept-legal', data);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to accept legal terms');
      }
    },
    
    async resetPassword(email: string): Promise<void> {
      const response = await api.post('/api/v1/auth/reset-password', { email });
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send reset email');
      }
    },
    
    async updatePassword(newPassword: string): Promise<void> {
      const response = await api.post('/api/v1/auth/update-password', { password: newPassword });
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update password');
      }
    },
    
    async resendVerification(): Promise<void> {
      const response = await api.post('/api/v1/auth/resend-verification');
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send verification email');
      }
    },

    async requestEmailChange(newEmail: string): Promise<void> {
      const response = await api.post('/api/v1/auth/change-email', { email: newEmail });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to change email');
      }
    },
    
    // Identity linking methods
    async getLinkedIdentities(): Promise<IdentitiesResponse> {
      const response = await api.get<IdentitiesResponse>('/api/v1/auth/identities');
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get linked identities');
      }
      
      return response.data;
    },
    
    async linkGoogle(): Promise<GoogleLinkResponse> {
      const response = await api.get<GoogleLinkResponse>('/api/v1/auth/link/google');
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get Google link URL');
      }
      
      return response.data;
    },
    
    async handleLinkCallback(): Promise<void> {
      throw new Error('Identity linking callback endpoint is not supported. Use redirect flow and refresh linked identities.');
    },
    
    async unlinkIdentity(provider: string): Promise<void> {
      const response = await api.delete(`/api/v1/auth/identities/${provider}`);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to unlink identity');
      }
    },
    
    async setPassword(password: string): Promise<void> {
      const response = await api.post('/api/v1/auth/set-password', { password });
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to set password');
      }
    },
    
    async hasPassword(): Promise<boolean> {
      const identities = await service.getLinkedIdentities();
      return identities.hasPassword;
    },
  };
  
  return service;
}
